import { useAtom, useSetAtom } from 'jotai';
import Axios, { AxiosResponse } from 'axios';
import { environment } from '../../environment';
import { errorCatcher, snackbarSettingsAtom } from '../../hooks';
import { useLogin } from '../login';
import {
    IReleaseNote,
    releaseNotesAtom,
    releaseNotesLoadingAtom,
    releaseNotesPublicAtom,
    latestReleaseVersionAtom
} from './types';

type ListReleaseNotes = () => Promise<void>;
type CreateReleaseNote = (payload: Omit<IReleaseNote, 'id' | 'created_at' | 'updated_at'>) => Promise<IReleaseNote | null>;
type UpdateReleaseNote = (id: number, payload: Partial<IReleaseNote>) => Promise<IReleaseNote | null>;
type DeleteReleaseNote = (id: number) => Promise<boolean>;

type ReleaseNoteKeyFunctions = {
    list: ListReleaseNotes;
    create: CreateReleaseNote;
    update: UpdateReleaseNote;
    remove: DeleteReleaseNote;
}

type UseReleaseNotesService = [
    IReleaseNote[],
    boolean,
    ReleaseNoteKeyFunctions
];

// #region useReleaseNotesService
export const useReleaseNotesService = (): UseReleaseNotesService => {
    const [releaseNotes, setReleaseNotes] = useAtom(releaseNotesAtom);
    const [loading, setLoading] = useAtom(releaseNotesLoadingAtom);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const list: ListReleaseNotes = async () => {
        setLoading(true);
        const response: AxiosResponse<IReleaseNote[]> | void = await Axios.get(`${environment.v2Url}/release-notes`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setReleaseNotes(response.data || []);
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to load release notes' });
        }
        setLoading(false);
    };

    const create: CreateReleaseNote = async (payload) => {
        const response: AxiosResponse<IReleaseNote> | void = await Axios.post(`${environment.v2Url}/release-notes`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Release note created' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to create release note' });
            return null;
        }
    };

    const update: UpdateReleaseNote = async (id, payload) => {
        const response: AxiosResponse<IReleaseNote> | void = await Axios.put(`${environment.v2Url}/release-notes/${id}`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Release note updated' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update release note' });
            return null;
        }
    };

    const remove: DeleteReleaseNote = async (id) => {
        const response: AxiosResponse<{ success: boolean }> | void = await Axios.delete(`${environment.v2Url}/release-notes/${id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Release note deleted' });
            await list();
            return true;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to delete release note' });
            return false;
        }
    };

    return [releaseNotes, loading, { list, create, update, remove }];
};

type ListPublicReleaseNotes = (limit?: number, offset?: number) => Promise<IReleaseNote[]>;
type GetLatestVersion = () => Promise<string | null>;

type PublicReleaseNotesFunctions = {
    listPublic: ListPublicReleaseNotes;
    getLatestVersion: GetLatestVersion;
}

type UsePublicReleaseNotes = [
    IReleaseNote[],
    boolean,
    PublicReleaseNotesFunctions
];

// #region usePublicReleaseNotes
export const usePublicReleaseNotes = (): UsePublicReleaseNotes => {
    const [publicNotes, setPublicNotes] = useAtom(releaseNotesPublicAtom);
    const [loading, setLoading] = useAtom(releaseNotesLoadingAtom);
    const setLatestVersion = useSetAtom(latestReleaseVersionAtom);
    const [, authToken, loginFunctions] = useLogin();

    const listPublic: ListPublicReleaseNotes = async (limit = 20, offset = 0) => {
        setLoading(true);
        const response: AxiosResponse<IReleaseNote[]> | void = await Axios.get(
            `${environment.v2Url}/release-notes/public?limit=${limit}&offset=${offset}`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        setLoading(false);
        if (response) {
            const notes = response.data || [];
            if (offset === 0) {
                setPublicNotes(notes);
            } else {
                setPublicNotes((prev) => [...prev, ...notes]);
            }
            return notes;
        }
        return [];
    };

    const getLatestVersion: GetLatestVersion = async () => {
        const response: AxiosResponse<{ version: string | null }> | void = await Axios.get(
            `${environment.v2Url}/release-notes/latest-version`,
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setLatestVersion(response.data?.version ?? null);
            return response.data?.version ?? null;
        }
        return null;
    };

    return [publicNotes, loading, { listPublic, getLatestVersion }];
};
